import type { RemoteSessionView, RemoteState, RemoteStatus } from '@api/remote/contracts';
import type { SearchHit } from '@contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Eye, FileText, MonitorSmartphone, ScreenShare } from 'lucide-react';
import { DropdownMenu } from 'radix-ui';
import { useNavigate } from 'react-router';
import { endRemoteSession, getTerminalTranscript, isStatus } from '../lib/api';
import { formatBytes } from '../lib/format';
import { absolute, ago, duration } from '../lib/time';
import { consentOutcomeText, sessionEndReason } from '../operations/audit';
import { useCapabilities } from '../operations/jobs';
import { describeConsent, useRemoteConsent } from './ConsentSetting';
import { transcriptText } from './terminal-session';
import { useScope } from '../scope/scope';
import { Button, buttonClass } from '../ui/Button';
import { cn } from '../ui/cn';
import { Card, EmptyState, SectionTitle, Tooltip, useNow } from '../ui/misc';
import { useToast } from '../ui/toast';

/** Why a session can't be opened right now, in words for the technician. */
const NOT_READY: Record<Exclude<RemoteState, 'ready' | 'not_configured'>, string> = {
  not_installed: 'The WITTCO agent installs remote access on its own; it isn’t on this device yet.',
  installing: 'Remote access is installing on this device. It will be ready in a minute or two.',
  failed: 'Remote access didn’t install on this device.',
  stopped: 'The remote access service on this device is stopped; the WITTCO agent will start it again.',
  offline: 'The device isn’t connected to WITTCO Remote right now. It may be off, asleep or offline.',
  unreachable: 'WITTCO Remote can’t be reached right now.',
};
export const remoteSummary = (status: RemoteStatus) =>
  status.state === 'ready' ? 'Ready: the device is connected to WITTCO Remote.'
    : status.state === 'not_configured' ? 'WITTCO Remote isn’t set up on this server.'
    : status.state === 'not_installed' && status.detail ? status.detail
    : `${NOT_READY[status.state]}${status.detail && (status.state === 'failed' || status.state === 'installing') ? ` (${status.detail})` : ''}`;

/** Opens the viewer in its own window, like a remote control client; falls back to this tab if pop-ups are blocked. */
export function useOpenViewer() {
  const { mspId } = useScope();
  const navigate = useNavigate();
  return (deviceId: string, mode: 'control' | 'view') => {
    const path = `/w/${mspId}/remote/${deviceId}${mode === 'view' ? '?mode=view' : ''}`;
    const opened = window.open(path, `wittco-remote-${deviceId}`, 'popup=yes,width=1366,height=860');
    if (!opened) navigate(path);
  };
}

/** The device header's "Remote in" button, with "View only" beside it. Shown only to people who may connect. */
export function RemoteInButton({ device, status }: { device: SearchHit; status: RemoteStatus | undefined }) {
  const open = useOpenViewer();
  const caps = useCapabilities(device);
  const { mspId } = useScope();
  const navigate = useNavigate();
  if (!status || status.state === 'not_configured' || !status.canConnect) return null;
  const ready = status.state === 'ready';
  if (status.desktopAvailable === false) {
    const terminal = () => {
      const path = `/w/${mspId}/terminal/${device.id}`;
      if (!window.open(path, `wittco-terminal-${device.id}`, 'popup=yes,width=1100,height=720')) navigate(path);
    };
    return <Tooltip content={status.desktopDetail ?? 'Connect to the terminal'}><span>
      <Button variant="brand" disabled={!ready || !caps.scripts || !caps.approve} onClick={terminal}>Open terminal</Button>
    </span></Tooltip>;
  }
  const button = (
    <div className="inline-flex">
      <button type="button" disabled={!ready} onClick={() => open(device.id, 'control')}
        className={buttonClass('brand', 'md', 'rounded-r-none disabled:opacity-60')}><ScreenShare className="size-4" />Remote in</button>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger disabled={!ready} aria-label="More ways to connect"
          className={buttonClass('brand', 'md', 'rounded-l-none border-l border-white/25 px-2 disabled:opacity-60')}><ChevronDown className="size-4" /></DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content align="end" sideOffset={6} className="z-50 w-64 rounded-xl border border-line bg-raised p-1 shadow-pop animate-pop">
            <DropdownMenu.Item onSelect={() => open(device.id, 'control')} className="cursor-pointer rounded-lg px-2.5 py-2 outline-none data-[highlighted]:bg-hover">
              <span className="flex items-center gap-2 text-[13.5px] font-medium"><ScreenShare className="size-4" />See and control</span>
              <span className="mt-0.5 block pl-6 text-[12px] text-ink-3">Use the device’s screen, keyboard and mouse. Approved with your security key.</span>
            </DropdownMenu.Item>
            <DropdownMenu.Item onSelect={() => open(device.id, 'view')} className="cursor-pointer rounded-lg px-2.5 py-2 outline-none data-[highlighted]:bg-hover">
              <span className="flex items-center gap-2 text-[13.5px] font-medium"><Eye className="size-4" />View only</span>
              <span className="mt-0.5 block pl-6 text-[12px] text-ink-3">Watch the screen; nothing you type or click reaches the device.</span>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
  return ready ? button : <Tooltip content={remoteSummary(status)}><span>{button}</span></Tooltip>;
}

/** The device's Remote tab: whether remote access works there, who is connected now, and recent sessions. */
export function RemotePanel({ device, status }: { device: SearchHit; status: RemoteStatus | undefined }) {
  const open = useOpenViewer();
  const now = useNow(5000);
  const consent = useRemoteConsent(device.organizationId);
  // Terminal transcripts (WRMM-68): your own, or anyone's where you could open a terminal yourself.
  const caps = useCapabilities(device);
  const canReadTranscripts = caps.remote && caps.scripts && caps.approve;
  if (!status) return <Card className="p-4 text-ink-3">Checking remote access…</Card>;
  const live = status.sessions.filter(s => s.active);
  const past = status.sessions.filter(s => !s.active);
  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="space-y-5">
        <Card className="flex flex-wrap items-center gap-4 p-4">
          <span className={cn('flex size-10 items-center justify-center rounded-xl', status.state === 'ready' ? 'bg-healthy-soft text-healthy' : 'bg-sunken text-ink-3')}>
            <MonitorSmartphone className="size-5" /></span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold">{status.state === 'ready' ? 'Remote access is ready' : 'Remote access isn’t available right now'}</p>
            <p className="mt-0.5 text-[13px] text-ink-2">{remoteSummary(status)} {status.desktopDetail}</p>
          </div>
          {status.canConnect && status.state === 'ready' && status.desktopAvailable !== false && (
            <div className="flex gap-2">
              <Button variant="brand" onClick={() => open(device.id, 'control')}><ScreenShare className="size-4" />Remote in</Button>
              <Button onClick={() => open(device.id, 'view')}><Eye className="size-4" />View only</Button>
            </div>
          )}
        </Card>
        {status.desktopAvailable === false && <RemoteInButton device={device} status={status} />}
        {live.length > 0 && (
          <section className="space-y-2">
            <SectionTitle>{live.every(s => s.connectedAt) ? 'Connected now' : 'In progress'}</SectionTitle>
            <Card className="divide-y divide-line">{live.map(s => <LiveSession key={s.id} session={s} now={now} canStop={status.canConnect} />)}</Card>
          </section>
        )}
        <section className="space-y-2">
          <SectionTitle>Recent sessions</SectionTitle>
          {past.length ? (
            <Card className="divide-y divide-line">
              {past.map(s => (
                <div key={s.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-[13.5px]">
                  <span className="w-28 shrink-0 font-medium">{s.mine ? 'You' : 'A teammate'}</span>
                  <span className="text-ink-2">{s.kind === 'terminal' ? 'Terminal' : s.mode === 'view' ? 'View only' : 'Control'}{s.durationSeconds !== null ? ` · ${duration(s.durationSeconds * 1000)}` : ''}</span>
                  <span className="text-ink-3">{s.endReason ? sessionEndReason[s.endReason] ?? s.endReason : ''}</span>
                  {s.consentOutcome && s.consentOutcome !== 'not_asked' && <span className="text-[12.5px] text-ink-3">· {consentOutcomeText[s.consentOutcome] ?? s.consentOutcome}</span>}
                  <span className="flex-1" />
                  {s.kind === 'terminal' && s.connectedAt && (s.mine || canReadTranscripts) && <TranscriptButton session={s} title={device.title} />}
                  {s.bytesFromDevice > 0 && <span className="text-[12.5px] tabular text-ink-3">{formatBytes(s.bytesFromDevice)}</span>}
                  <Tooltip content={absolute(s.requestedAt)}><span className="text-[12.5px] text-ink-3">{ago(s.requestedAt, now)}</span></Tooltip>
                </div>
              ))}
            </Card>
          ) : <Card><EmptyState icon={<ScreenShare className="size-5" />} title="No sessions yet">Sessions on this device show here, and in the audit trail, with who connected and for how long.</EmptyState></Card>}
        </section>
      </div>
      <Card className="h-fit p-4 text-[13px] leading-relaxed text-ink-2" as="aside">
        <SectionTitle>How remote access works</SectionTitle>
        <ul className="mt-2 list-disc space-y-1.5 pl-4">
          {status.desktopConsent === 'portal' ? (consent.data && !consent.data.ask
            ? <li><span className="font-medium text-ink">{device.organizationName ?? 'This customer'}:</span> technicians are let in at once, so GNOME shares the main monitor without asking. Taking control of a locked computer unlocks its screen for the session and locks it again when you disconnect; a view-only session can’t unlock it. Nobody signed in: use a terminal.</li>
            : <li>GNOME asks the signed-in person to select a monitor and share it. For control, they must also enable “Allow Remote Interaction”. An unanswered request grants no access. A locked desktop can’t be shared.</li>)
            : consent.data && <li><span className="font-medium text-ink">{device.organizationName ?? 'This customer'}:</span> {describeConsent(consent.data)}</li>}
          {status.desktopAvailable !== false && <li>{status.desktopConsent === 'portal'
            ? 'The person at the computer sees GNOME’s screen-sharing indicator and can stop sharing there.'
            : 'The person at the computer sees a “WITTCO Connected” tab at the side of their screen during a desktop connection, and can end that session there.'}</li>}
          <li>Taking control is approved with your security key or passkey, once per session. A view-only desktop needs no approval: nothing you type or click reaches the device. Terminals, including tmux viewing, always require a key approval; they aren’t announced to or asked of the person at the computer, and are recorded.</li>
          <li>Sessions go through WITTCO RMM, which checks your access when you connect and every 15 seconds after; losing access ends the session.</li>
          <li>Every session is recorded with who connected, when, for how long and how it ended.</li>
          {status.agentVersion && <li>Remote access version {status.agentVersion}{status.reportedAt ? `, checked ${ago(status.reportedAt, now)}` : ''}.</li>}
        </ul>
      </Card>
    </div>
  );
}

function LiveSession({ session, now, canStop }: { session: RemoteSessionView; now: number; canStop: boolean }) {
  const { mspId } = useScope();
  const client = useQueryClient();
  const toast = useToast();
  const stop = useMutation({ mutationFn: () => endRemoteSession(mspId, session.id),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['scope', mspId, 'remote', session.deviceId] }),
    onError: error => toast({ tone: 'warning', title: 'The session wasn’t stopped', body: isStatus(error, 403) ? 'You need remote access to this device to stop it.' : 'Try again.' }) });
  // Not let in yet: still connecting, or waiting for the person at the computer (nothing is shown or passed meanwhile).
  const tone = session.connectedAt ? 'bg-healthy' : 'bg-[#F07522]';
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3">
      <span className="relative flex size-2.5"><span className={cn('absolute inline-flex size-full animate-ping rounded-full opacity-60', tone)} /><span className={cn('relative inline-flex size-2.5 rounded-full', tone)} /></span>
      <span className="font-medium">{session.mine ? 'You' : 'A teammate'}</span>
      <span className="text-[13px] text-ink-2">{session.connectedAt
        ? `${session.kind === 'terminal' ? 'in a Terminal' : session.mode === 'view' ? 'watching' : 'in control'} for ${duration(now - Date.parse(session.connectedAt))}`
        : session.consent.ask ? 'waiting for the person at the computer to allow it' : 'connecting'}</span>
      <span className="flex-1" />
      {canStop && <Button size="sm" variant="danger" disabled={stop.isPending} onClick={() => stop.mutate()}>{stop.isPending ? 'Stopping…' : 'Stop session'}</Button>}
    </div>
  );
}

/** Downloads a terminal session's transcript as text (WRMM-68); the server records that it was read. */
function TranscriptButton({ session, title }: { session: RemoteSessionView; title: string }) {
  const { mspId } = useScope();
  const toast = useToast();
  const heading = `Terminal on ${title}, ${absolute(session.connectedAt ?? session.requestedAt)} (session ${session.id})`;
  const download = useMutation({ mutationFn: async () => transcriptText(await getTerminalTranscript(mspId, session.id), heading),
    onSuccess: text => {
      const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
      const link = Object.assign(document.createElement('a'), { href: url, download: `terminal-${title}-${session.id.slice(0, 8)}.txt` });
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    onError: error => toast({ tone: 'warning', title: 'The transcript couldn’t be read', body: isStatus(error, 403) ? 'Reading it takes what opening a terminal on this device takes.' : 'Try again.' }) });
  return <Button size="sm" variant="secondary" disabled={download.isPending} onClick={() => download.mutate()}><FileText className="size-3.5" />{download.isPending ? 'Preparing…' : 'Transcript'}</Button>;
}
